// apps/client/src/state/slices/museum.ts (M2-T10) — keys start with "museum" (§8.7): the hall state from the museum snapshot and
// `museum.donated`, what in your pockets the curator can identify or take, and the identify and donate flows.
import type { ItemId, ItemView, MuseumEntryView, MuseumState } from '@whiskerton/shared';
import { getItem, t } from '@whiskerton/content';
import { UNKNOWN_FOSSIL, canDonate } from '@whiskerton/shared/rules/museum';
import { getGame } from '../../game/instance';
import type { SliceCreator } from '../types';

export interface MuseumSlice {
  museumState: MuseumState | null;
  museumSetState(s: MuseumState): void;
  museumAddEntry(kind: 'fossil' | 'fish', e: MuseumEntryView): void;
  museumUnidentified(): ItemView[];
  museumDonatable(): ItemView[];
  museumIdentifyAll(): Promise<ItemView[] | null>;
  /** A refusal the curator answers in his own voice comes back as `rejected` (no toast, G13); any other refusal toasts and is null. */
  museumDonate(item: ItemId): Promise<{ hallComplete: 'fossils' | 'fish' | null } | { rejected: CuratorRefusal } | null>;
}
/** Refusals Professor Reed voices himself (curator.alreadyDonated / notDonatable / unidentified), G13. */
export type CuratorRefusal = 'already_donated' | 'not_donatable' | 'unidentified';
const CURATOR_REFUSALS: readonly CuratorRefusal[] = ['already_donated', 'not_donatable', 'unidentified'];
// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- declaration merging into GameStore
declare module '../types' { interface GameStore extends MuseumSlice {} }

const donatedIds = (s: MuseumState | null) => new Set([...(s?.fossils ?? []), ...(s?.fish ?? [])].map((e) => e.catalogId));

export const slice: SliceCreator<MuseumSlice> = (set, get) => ({
  museumState: null,
  museumSetState: (s) => set({ museumState: s }),
  museumAddEntry(kind, e) {
    const cur = get().museumState ?? { fossils: [], fish: [] };
    const key = kind === 'fossil' ? 'fossils' : 'fish';
    if (cur[key].some((x) => x.catalogId === e.catalogId)) return;
    set({ museumState: { ...cur, [key]: [...cur[key], e] } });
  },
  museumUnidentified: () => (get().you?.items ?? []).filter((i) => i.at.loc === 'pockets' && i.catalogId === UNKNOWN_FOSSIL),
  museumDonatable() {
    const done = donatedIds(get().museumState);
    return (get().you?.items ?? []).filter((i) => (i.at.loc === 'pockets' || i.at.loc === 'hand') && canDonate(i.catalogId, done) === 'ok');
  },
  async museumIdentifyAll() {
    const ids = get().museumUnidentified().map((i) => i.id);
    if (ids.length === 0) return [];
    const res = await getGame().cmd.act('museum.identify', { items: ids });
    return res.ok ? res.r.items : null;
  },
  async museumDonate(item) {
    // The action client toasts every other reject as usual (and never a timeout or going offline).
    const res = await getGame().cmd.act('museum.donate', { item }, { silent: CURATOR_REFUSALS });
    if (!res.ok) return (CURATOR_REFUSALS as readonly string[]).includes(res.code) ? { rejected: res.code as CuratorRefusal } : null;
    get().museumAddEntry(getItem(res.r.entry.catalogId).kind === 'fish' ? 'fish' : 'fossil', res.r.entry);
    if (res.r.hallComplete) get().toast('museum.hallCompleteToast', { hall: t(res.r.hallComplete === 'fossils' ? 'curator.hallFossilsName' : 'curator.hallFishName') });
    return { hallComplete: res.r.hallComplete };
  },
});
